import { engineComponent, ArrayUtils } from "neo-common";
import { Communication } from "neo-games";
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";
import { Symbols } from "./Symbols";
import { Reel } from "./Reel";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../Utils/FrameTimer";
import { Configuration } from "../Components/Configuration";
import { ComponentTypes } from "../Components/ComponentTypes";
import { PlayList } from "../Components/Sounds/PlayList";
import { AppContext } from "../../Common/AppContext";
import { IReelTeasingAttributes, TeasingComponent } from "../CustomComponents/TeasingComponent";
import { TeasingBonus } from "../CustomComponents/TeasingBonus";
import { ITweenable, Tweens } from "../Components/Effects/Tweens";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { GameManager } from "../Managers/GameManager";
import { GameData } from "../Channels/Common/Datas/GameData";
import { DataManager } from "../Managers/DataManager";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";

type IReel = Communication.Game.IReel;

/**
 * Machine handle reels and symbols for reels rotation.
 * Machine define behaviour for complete flow of reel rotation.
 * @class Machine
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class Machine extends BaseElement {
    // events for view when reel or machine stopped
    static MachineStopped: string = "machineStopped";
    static ReelStopped: string = "onReelStopped";
    // min spin rotation in frames
    public _minSpinRotation: number;
    // min spin rotation in frames for high wins
    public _minSpinRotationLong: number;
    // information for view which reel was stopped actualy
    actualReelStopped: number;
    public _winShakes: number;
    public _winShakesRollY: Array<number>;
    // number of shakes
    public _spinShakes: number;
    public _spinBeginEndShakes: number;
    public _spinShakesRollX: Array<number>;
    public _teasingStopDelay: number;
    // symbols from view
    private _symbols: Symbols;
    // array of reel indexes
    private _reels: Array<Reel>;
    // array of stopped symbols for finish reel rotation
    private _stoppedSymbolsArray: Array<IReel>;
    // count of reel
    private _reelCount: number;
    // reels x positions
    private _reelX: Array<number>;
    // reels y positions
    private _reelY: Array<number>;
    // symbol width without offsetX
    private _symbolWidth: number;
    // symbol height without offsetY
    private _symbolHeight: number;
    // number of symbols which will be visible on screen during the rotation
    private _visibleSymbolCount: number;
    // delay to start rotation in frames
    private _startDelay: number;
    // delay to stop rotation in frames
    private _stopDelay: number;
    // configuration ID for reels
    private _reelConfiguration: string;
    // timer for minimal time to stop reels
    private _stopTimer: FrameTimerEventEmitter;
    // timer for defined time in configuration.json to stop reels
    private _legalStopTimer: number;

    // Custom
    // boolean which indicates that machine can stop and rotate min defined time
    private _stoppedFromMachine: boolean;
    // boolean which indicates that machine can stop and response has arrived
    private _stoppedFromServer: boolean;
    // boolean which indicates that machine can stop and rotate min legal time
    private _stoppedFromLegal: boolean;

    protected _startSpinTime: number;
    // container as a parent for reels
    private _reelsContainer: PIXI.Container;
    // helper to count stopped reels
    private _stoppedReelIndexes: number;
    private force: boolean;
    private _teasingComponent: TeasingBonus;
    private _bg: SwitchElement;
    private teasingComponent: TeasingBonus;

    private rta: Array<IReelTeasingAttributes>;

    private spinRotation: number;

    private longRotation: boolean;

    constructor() {
        super();

        // default values
        this.longRotation = false;
        this._reels = [];
        this._stoppedSymbolsArray = [];
        this._stoppedFromServer = false;
        this._stoppedFromMachine = false;
        this._stoppedFromLegal = false;
        this._stoppedReelIndexes = 0;

        this.spinRotation = 0;
        this._stopTimer = CreateFrameTimer("Machine");
        this._stopTimer.on(FrameTimerEvents.Complete, this.spinStop.bind(this));

        this.actualReelStopped = -1;

        this._reelsContainer = new PIXI.Container();
        this.addChild(this._reelsContainer);

        this.force = false;

        this.teasingComponent = new TeasingBonus();
    }

    static spinPostRoll(item: any, beginEndCoords: any): void {
        Tweens.removeTweens(item, "position");
        Tweens.positionTween(item, {
            bezier: {
                values: beginEndCoords,
            },
            tweenX: [item.position.x, item.position.x],
            tweenY: [item.position.y, item.position.y],
            tweenDuration: 5,
            tweenDelay: 0,
        });
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    public createByNode(node: Element): void {
        super.createByNode(node);

        if (this.maskSprite != null && this._reelsContainer != null) {
            this._reelsContainer.addChild(this.maskSprite);
            this._reelsContainer.mask = this.maskSprite;
        }

        if (node.hasAttribute(MachineAttributes.CONFIGS)) {
            let configs: Array<string> = node.getAttribute(MachineAttributes.CONFIGS).split(";");
            for (let i: number = 0; i < configs.length; i++) {
                let config: Element = Configuration.Configurations[configs[i]];
                if (config != null) {
                    this.configurate(config);
                }
            }
        }
    }

    /**
     * Initialize reference correct symbols and create reels.
     * @method initialize
     * @param symbols Symbols
     * @param reels Array<Array<number>>
     * @return void
     */
    public initialize(symbols: Symbols, reels: Array<Array<number>>, teasingComponent?: any, bg?: any): void {
        if (teasingComponent != null) {
            this._teasingComponent = teasingComponent;
        } else {
            this._teasingComponent = new TeasingBonus();
        }

        this._bg = bg;
        this._symbols = symbols;
        this.createReels(reels);
    }

    /**
     * Set symbols which stopped after reels will be stopped.
     * @method setStoppedSymbolsArray
     * @param value Array<number>
     * @return void
     */
    public setStoppedSymbolsArray(value: Array<IReel>): void {
        this._stoppedSymbolsArray = value;
    }

    /**
     * Show static symbols on reels.
     * @method showSymbols
     * @param reelsData Array<IReel>
     * @return void
     */
    public showSymbols(reelsData: Array<IReel>): void {
        if (DEBUG) {
            this.log("showSymbols");
        }

        this.actualReelStopped = -1;

        if (this._reels != null) {
            for (let i: number = 0; i < this._reelCount; i++) {
                if (reelsData[i] != null) {
                    if (this._reels[i] != null) {
                        this._reels[i].visible = true;
                        this._reels[i].setData(reelsData[i]);
                        this._reels[i].showSymbols();
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no reelsData with index: " + i);
                    }
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no reels");
            }
        }
    }

    public spinReelsImages(reels: any, spinRotationLong?: number): void {
        if (this._spinShakes != 0) {
            let stopDelay = this._stopDelay;
            let reelTeasingAttributes = this.teasingComponent.checkOnInit({
                force: this.force,
                stoppedSymbols: this._stoppedSymbolsArray,
            });

            this.rta = reelTeasingAttributes;
            for (let i = 0; i < reels.children.length; i++) {
                let spinShakes = this._spinShakes;
                let spinBeginEndsShakes = this._spinBeginEndShakes;
                if (reels.children[i].name != "ReelBG") {
                    let item: ITweenable = <any>reels.children[i];
                    let coords: Array<any> = [];
                    let beginEndCoords: Array<any> = [];
                    let number = 0;

                    if (this._spinShakesRollX.length > 1) {
                        number =
                            Math.random() * (this._spinShakesRollX[1] - this._spinShakesRollX[0]) +
                            this._spinShakesRollX[0];
                    } else {
                        number = this._spinShakesRollX[0];
                    }

                    if (i % 2 == 1) number *= -1;

                    if (reelTeasingAttributes[i - 1].stopDelay != 0) {
                        stopDelay = reelTeasingAttributes[i - 1].stopDelay + this.spinRotation + 30;
                    } else {
                        stopDelay = this.spinRotation + 30;
                    }

                    if (!this.checkDoubleFuits(this._stoppedSymbolsArray)) {
                        stopDelay = reelTeasingAttributes[i - 1].stopDelay + this.spinRotation + 30;
                    }

                    spinShakes = spinShakes / 60;

                    spinShakes = Math.floor(spinShakes * stopDelay);

                    beginEndCoords.push({ x: item.position.x, y: item.position.y });
                    let numb = number * 4;
                    for (let k = 0; k < spinBeginEndsShakes; k++) {
                        if (k % 2 == 1) numb *= -1;
                        beginEndCoords.push({ x: item.position.x + numb, y: item.position.y });
                    }
                    beginEndCoords.push({ x: item.position.x, y: item.position.y });

                    coords.push({ x: item.position.x, y: item.position.y });
                    for (let k = 0; k < spinShakes; k++) {
                        if (k % 2 == 1) number *= -1;

                        coords.push({ x: item.position.x + number, y: item.position.y });
                    }
                    coords.push({ x: item.position.x, y: item.position.y });

                    Tweens.removeTweens(item, "position");

                    Tweens.positionTween(item, {
                        bezier: {
                            values: beginEndCoords,
                        },
                        tweenX: [item.position.x, item.position.x],
                        tweenY: [item.position.y, item.position.y],
                        tweenDuration: 5,
                        tweenDelay: 0,
                        callbackFunction: this.spinRoll,
                        callbackParams: [item, coords, stopDelay, beginEndCoords, this.longRotation],
                        callbackScope: null,
                    });
                }
            }
        }
    }

    spinRoll(item: any, coords: any, stopDelay: any, beginEndCoords: any, longRotation: boolean): void {
        if (longRotation) {
            stopDelay += 30;
        } else {
            stopDelay -= 30;
        }

        Tweens.removeTweens(item, "position");
        Tweens.positionTween(item, {
            bezier: {
                values: coords,
            },
            tweenX: [item.position.x, item.position.x],
            tweenY: [item.position.y, item.position.y],
            tweenDuration: stopDelay,
            tweenDelay: 0,
            callbackFunction: Machine.spinPostRoll,
            callbackParams: [item, beginEndCoords],
            callbackScope: null,
        });
    }

    public removeTeasing(teasingElement: any): void {
        if (teasingElement.children.length > 0) {
            for (let i = 0; i < teasingElement.children.length; i++) {
                let node: SwitchElement = <any>teasingElement.children[i];
                node.goToCase("NORMAL");
            }
        }
    }

    /**
     * Start spinning reels.
     * @method spinStart
     * @param longRotation boolean
     * @return void
     */
    public spinStart(longRotation?: boolean, minSpinRotation?: any): void {
        if (DEBUG) {
            this.log("spinStart");
        }

        this._stoppedFromServer = false;
        this._stoppedFromMachine = false;
        this._stoppedFromLegal = false;

        this._startSpinTime = Date.now();

        this.force = false;

        this.visible = true;
        this._reelsContainer.visible = true;

        this.actualReelStopped = -1;
        this._stoppedReelIndexes = 0;

        this.spinRotation = minSpinRotation;

        if (this._reels != null) {
            for (let i: number = 0; i < this._reelCount; i++) {
                if (this._reels[i] != null) {
                    if (this._stoppedSymbolsArray.length > 0) {
                        this._reels[i].visible = true;
                        this._reels[i].setData(this._stoppedSymbolsArray[i]);
                        this._reels[i].showSymbols();
                        this._reels[i].spinStart();
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no stopped symbols");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no reel with index: " + i);
                    }
                }
            }

            if (longRotation != null && longRotation) {
                this.longRotation = longRotation;
                this._stopTimer.start(minSpinRotation + this._minSpinRotationLong);
            } else {
                this._stopTimer.start(minSpinRotation);
                this.longRotation = false;
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no reels");
            }
        }

        PlayList.play("SND_BaseRotationStart");
        PlayList.loop("SND_BaseRotation", 0, true);
    }

    protected verifyMinGameRoundDurationConditions(): boolean {
        return (
            AppContext.gameConfiguration.MinGameRoundDuration > 0 || AppContext.gameConfiguration.GameRoundMinSpan > 0
        );
    }

    /**
     * Stop reels rotation.
     * @method spinStop
     * @param event any - from timer
     * @param force boolean - default false used when spin_stop was dispatch
     * @param fromServer boolean - default false used when spin_stop was dispatch from server
     * @param legal
     * @return void
     */
    public spinStop(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        if (fromServer) {
            this._stoppedFromServer = true;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            if (this.verifyMinGameRoundDurationConditions()) {
                this._legalStopTimer = setTimeout(
                    () => this.spinStop(null, false, false, true),
                    this.getLegalRemainingSpinDuration(),
                );
            } else {
                this._stoppedFromLegal = true;
            }
        } else if (legal) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
        } else {
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }
        // this.removeTeasing(this._teasingComponent)
        if (force) {
            this.force = force;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
            this._stopTimer.stop();
            this._stoppedFromMachine = true;

            PlayList.stop("SND_BaseX2");

            this.removeTeasing(this._teasingComponent);
            // PlayList.stop('SND_BaseTeaser')

            this._bg.goToCase("OFF");
        }

        if (DEBUG) {
            this.log("spinStop: " + force);
        }

        if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
            if (!(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")) {
                DataManager.updateDataElement(GameData.DataTotalWin, "0");
                DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            }
            if (this._reels != null) {
                for (let i: number = 0; i < this._reelCount; i++) {
                    if (this._reels[i] != null) {
                        if (this._stoppedSymbolsArray.length !== 0) {
                            this._reels[i].setData(this._stoppedSymbolsArray[i]);

                            if (typeof this.rta !== "undefined") {
                                this._reels[i].spinStop(force, this.rta[i].stopDelay);
                            } else {
                                this._reels[i].spinStop(force);
                            }
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no stopped symbols");
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }
        }
    }

    /**
     * Get remaining time for legal spin duration
     * @method getLegalRemainingSpinDuration
     * @return number
     */
    protected getLegalRemainingSpinDuration(): number {
        if (AppContext.gameConfiguration.GameRoundMinSpan > 0) {
            return this.calculateRemainingSpinDuration(AppContext.gameConfiguration.GameRoundMinSpan);
        }

        if (AppContext.gameConfiguration.MinGameRoundDuration > 0) {
            return AppContext.gameConfiguration.MinGameRoundDuration;
        }

        return 0;
    }

    /**
     * Calculate remaining time for legal spin duration
     * @method calculateRemainingSpinDuration
     * @param minTime: number - minimal spin duration in seconds
     * @return number
     */
    protected calculateRemainingSpinDuration(minTime: number): number {
        const now = Date.now();
        const diff = now - this._startSpinTime;
        const reelStopDuration = this.getReelStopDuration();
        return Math.max(minTime - diff - reelStopDuration, 0);
    }

    protected getReelStopDuration(): number {
        return (this._reels[this._reelCount - 1].getStopDuration() / 60) * 1000;
    }

    /**
     * Dispatched onMachineStopped when all reels are stopped.
     * @method onReelStopped
     * @param reelIndex number - reel index
     * @return void
     */
    public onReelStopped(reelIndex: number): void {
        if (DEBUG) {
            this.log("reel stopped: " + reelIndex);
        }

        PlayList.play("SND_BaseRotationEnd" + reelIndex);

        this._stoppedReelIndexes++;
        this.actualReelStopped = reelIndex;
        this._reels[reelIndex].visible = false;

        if (this._teasingComponent != null) {
            if (this.checkDoubleFuits(this._stoppedSymbolsArray, reelIndex)) {
                if (!this.force) {
                    if (reelIndex != 0) this._bg.goToCase("WIN1" + reelIndex);
                    this.teasingComponent.animateTeasing(reelIndex, this._teasingComponent);
                    if (reelIndex == 1) {
                        PlayList.play("SND_BaseTeaser");
                        PlayList.play("SND_BaseTeaserStart");
                        PlayList.stop("SND_BaseLoop");
                    }
                } else {
                    PlayList.stop("SND_BaseTeaser");
                    PlayList.stop("SND_BaseTeaserStart");
                    this._bg.goToCase("OFF");
                }
            }
        }

        this.emit(Machine.ReelStopped);

        if (this._stoppedReelIndexes === this._reelCount) {
            if (this._teasingComponent != null) {
                if (this.checkDoubleFuits(this._stoppedSymbolsArray)) {
                    if (!this.force) {
                        this.teasingComponent.finishTeasing(reelIndex, this._teasingComponent);
                    }
                }

                if (reelIndex == 2 && this.rta[reelIndex].action == 2 && GameManager.baseResponse.currentWin == 0)
                    PlayList.loop("SND_BaseLoop");
            }

            PlayList.stop("SND_BaseRotation", 10);
            PlayList.stop("SND_BaseTeaser");
            PlayList.stop("SND_BaseTeaserStart");
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stopTimer.stop();
            if (DEBUG) {
                const old = AppContext.gameConfiguration.MinGameRoundDuration;
                const current = AppContext.gameConfiguration.GameRoundMinSpan;
                const minDuration = current > 0 ? current : old > 0 ? old : 0;
                const spinDuration = Date.now() - this._startSpinTime;

                if (spinDuration > minDuration || !this.verifyMinGameRoundDurationConditions()) {
                    this.log("Spin time duration: " + spinDuration + " ms");
                } else {
                    console.log("%cError spin time duration:" + spinDuration + " ms", "color: #ff9900");
                }
            }

            this.emit(Machine.MachineStopped);
            this._reelsContainer.visible = false;
        }
    }

    public checkDoubleFuits(stoppedSymbolsArray: Array<any>, reelIndex?: any) {
        let allowedSymbolsForTeasing = [2, 3, 4, 5];
        let actualSymbol = 0;
        let isFruits = false;

        for (let i = 0; i <= reelIndex; i++) {
            if (
                stoppedSymbolsArray[i].symbols[0] == stoppedSymbolsArray[0].symbols[0] &&
                stoppedSymbolsArray[i].symbols[0] == stoppedSymbolsArray[i].symbols[1] &&
                stoppedSymbolsArray[i].symbols[0] == stoppedSymbolsArray[i].symbols[2] &&
                allowedSymbolsForTeasing.indexOf(stoppedSymbolsArray[i].symbols[0]) > -1
            ) {
                if (actualSymbol == 0) {
                    actualSymbol = stoppedSymbolsArray[i].symbols[0];
                }
                if (actualSymbol == stoppedSymbolsArray[i].symbols[0]) {
                    isFruits = true;
                }
            } else {
                return false;
            }
        }

        return isFruits;
    }

    public getStopDelayTimes(stoppedSymbolsArray: Array<any>) {
        if (
            stoppedSymbolsArray[0].symbols[0] == stoppedSymbolsArray[0].symbols[1] &&
            stoppedSymbolsArray[0].symbols[0] == stoppedSymbolsArray[0].symbols[2]
        ) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Get attributes from configuration and set up supported properties.
     * @method configurate
     * @param node is Element from xml
     * @return void
     */
    private configurate(node: Element): void {
        /*
         * ReelCount is count of reels on screen for game.
         * @property ReelCount
         * @type Number
         * @default null
         */
        if (node.hasAttribute("ReelCount")) {
            this._reelCount = parseInt(node.getAttribute("ReelCount"));
        }

        /*
         * ReelX is array of x position for reels.
         * @property ReelX
         * @type Array<number>
         * @default null
         */
        if (node.hasAttribute("ReelX")) {
            this._reelX = ArrayUtils.numberArray(node.getAttribute("ReelX"));
        }

        /*
         * ReelY is array of y position for reels.
         * @property ReelY
         * @type Array<number>
         * @default null
         */
        if (node.hasAttribute("ReelY")) {
            this._reelY = ArrayUtils.numberArray(node.getAttribute("ReelY"));
        }

        /*
         * Standard symbol width without offsets.
         * @property SymbolWidth
         * @type number
         * @default null
         */
        if (node.hasAttribute("SymbolWidth")) {
            this._symbolWidth = parseInt(node.getAttribute("SymbolWidth"));
        }

        /*
         * Standard symbol height without offsets.
         * @property SymbolHeight
         * @type number
         * @default null
         */
        if (node.hasAttribute("SymbolHeight")) {
            this._symbolHeight = parseInt(node.getAttribute("SymbolHeight"));
        }

        /*
         * Number of symbols visible on reel.
         * @property VisibleSymbolCount
         * @type number
         * @default null
         */
        if (node.hasAttribute("VisibleSymbolCount")) {
            this._visibleSymbolCount = parseInt(node.getAttribute("VisibleSymbolCount"));
        }

        /*
         * Min spin rotation in frames.
         * @property MinSpinRotation
         * @type number
         * @default 40
         */
        if (node.hasAttribute("MinSpinRotation")) {
            this._minSpinRotation = parseInt(node.getAttribute("MinSpinRotation"));
        } else {
            this._minSpinRotation = 40;
        }

        /*
         * Min spin rotation in frames for outro high wins.
         * @property MinSpinRotationLong
         * @type number
         * @default 100
         */
        if (node.hasAttribute("MinSpinRotationLong")) {
            this._minSpinRotationLong = parseInt(node.getAttribute("MinSpinRotationLong"));
        } else {
            this._minSpinRotationLong = 100;
        }

        /*
         * Delay to start rotation in frames.
         * @property StartDelay
         * @type number
         * @default 3
         */
        if (node.hasAttribute("StartDelay")) {
            this._startDelay = parseInt(node.getAttribute("StartDelay"));
        } else {
            this._startDelay = 3;
        }

        /*
         * Delay to stop rotation in frames.
         * @property StopDelay
         * @type number
         * @default 10
         */
        if (node.hasAttribute("StopDelay")) {
            this._stopDelay = parseInt(node.getAttribute("StopDelay"));
        } else {
            this._stopDelay = 10;
        }

        /*
         * Configuration ID for reels.
         * @property StopDelay
         * @type string
         * @default null
         */
        if (node.hasAttribute("ReelConfiguration")) {
            this._reelConfiguration = node.getAttribute("ReelConfiguration");
        }

        /*
         * Custom
         */

        if (node.hasAttribute("WinShakes")) {
            this._winShakes = parseInt(node.getAttribute("WinShakes"));
        }

        if (node.hasAttribute("WinShakesRollY")) {
            this._winShakesRollY = ArrayUtils.numberArray(node.getAttribute("WinShakesRollY"), ";");
        }

        if (node.hasAttribute("SpinShakes")) {
            this._spinShakes = parseInt(node.getAttribute("SpinShakes"));
        }

        if (node.hasAttribute("SpinBeginEndShakes")) {
            this._spinBeginEndShakes = parseInt(node.getAttribute("SpinBeginEndShakes"));
        }

        if (node.hasAttribute("SpinShakesRollX")) {
            this._spinShakesRollX = ArrayUtils.numberArray(node.getAttribute("SpinShakesRollX"), ";");
        }
        if (node.hasAttribute("TeasingStopDelay")) {
            this._teasingStopDelay = parseInt(node.getAttribute("TeasingStopDelay"));
        }
    }

    /**
     * Create reels based on response and xml configuration.
     * @method createReels
     * @param reels Array<Array<number>> from initialization and based on response
     * @return void
     */
    private createReels(reels: Array<Array<number>>): void {
        for (let i: number = 0; i < this._reelCount; i++) {
            let reel: Reel = new Reel();
            if (this._reelConfiguration != null) {
                let configs: Array<string> = this._reelConfiguration.split(";");
                for (let j: number = 0; j < configs.length; j++) {
                    reel.configure(Configuration.Configurations[configs[j]]);
                }
            }
            reel.visible = false;
            reel.initialize(
                i > 0 ? this._reels[i - 1] : null,
                i < this._reelCount - 1 ? this._reels[i + 1] : null,
                i,
                this._symbols,
                reels[i],
                this._reelX[i],
                this._reelY[i],
                this._visibleSymbolCount,
                1,
                1,
                this._symbolWidth,
                this._symbolHeight,
                this._startDelay * i,
                this._stopDelay * i,
            );
            reel.on(Reel.ReelStopped, this.onReelStopped.bind(this));
            this._reelsContainer.addChild(reel);
            this._reels.push(reel);
        }
        this._reelsContainer.visible = false;
    }
}

/**
 * Can inherit attributes from BaseElement
 */
export const MachineAttributes = {
    /**
     * Name of config for machine.
     * @property Configs
     * @type string
     * @default null
     */
    CONFIGS: "Configs",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Machine.ts
 **/